import {
    Pagination,
    PaginationContent,
    PaginationItem,
    PaginationLink,
} from "@/components/ui/pagination";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";

interface ModernPaginationProps {
    page: number;
    totalPages: number;
    onPageChange: (page: number) => void;
    total?: number;
    limit?: number;
}

export const ModernPagination = ({ page, totalPages, onPageChange, total, limit }: ModernPaginationProps) => {
    if (totalPages <= 1) return null;

    const renderPaginationItems = () => {
        const pages = [];
        const showMax = 5;

        if (totalPages <= showMax) {
            for (let i = 1; i <= totalPages; i++) pages.push(i);
        } else {
            pages.push(1);
            if (page > 3) pages.push("ellipsis-1");
            const start = Math.max(2, page - 1);
            const end = Math.min(totalPages - 1, page + 1);
            for (let i = start; i <= end; i++) {
                if (!pages.includes(i)) pages.push(i);
            }
            if (page < totalPages - 2) pages.push("ellipsis-2");
            pages.push(totalPages);
        }

        return pages.map((p) => {
            if (typeof p === "string") {
                return (
                    <PaginationItem key={p}>
                        <span className="flex h-9 w-9 items-center justify-center">...</span>
                    </PaginationItem>
                );
            }
            return (
                <PaginationItem key={p}>
                    <PaginationLink
                        onClick={() => onPageChange(p)}
                        isActive={page === p}
                        className="cursor-pointer"
                    >
                        {p}
                    </PaginationLink>
                </PaginationItem>
            );
        });
    };

    return (
        <div className="mt-4 flex items-center justify-center px-4">
            <Pagination className="w-auto mx-0">
                <PaginationContent>
                    <PaginationItem>
                        <PaginationLink
                            aria-label="Go to previous page"
                            size="default"
                            className={cn(
                                "gap-1 pl-2.5 cursor-pointer",
                                page === 1 && "pointer-events-none opacity-50"
                            )}
                            onClick={() => onPageChange(Math.max(1, page - 1))}
                        >
                            <ChevronLeft className="h-4 w-4" />
                            <span className="hidden sm:inline">Previous</span>
                        </PaginationLink>
                    </PaginationItem>
                    {renderPaginationItems()}
                    <PaginationItem>
                        <PaginationLink
                            aria-label="Go to next page"
                            size="default"
                            className={cn(
                                "gap-1 pr-2.5 cursor-pointer",
                                page === totalPages && "pointer-events-none opacity-50"
                            )}
                            onClick={() => onPageChange(Math.min(totalPages, page + 1))}
                        >
                            <span className="hidden sm:inline">Next</span>
                            <ChevronRight className="h-4 w-4" />
                        </PaginationLink>
                    </PaginationItem>
                </PaginationContent>
            </Pagination>
        </div>
    );
};
